import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  AccordionMode,
  AccordionSchemeDefinition,
  AccordionSchemeName,
  AccordionSize
} from "./constants/accordion-types";
import {
  ACCORDION_MODE_MULTIPLE,
  ACCORDION_MODE_SINGLE,
  ACCORDION_SCHEME_CONFIG,
  ACCORDION_SCHEME_MAP
} from "./constants/accordion-constants";
import { KeyIds } from "../../constants/keys";
import { isDesktopOrLarger, isMobile, isTablet } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { AccordionSchemeConfiguration } from "../../constants/colors";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [dynamic] - Accordion uses a dynamic number of named slots, each holding a single eon-ui-accordion-pane as child. Use a unique slot name per pane! Panes have a single (unnamed) slot and may hold any number of HTML content.
 */
@Component({
  tag: "eon-ui-accordion",
  styleUrl: "accordion.scss",
  shadow: true
})
export class Accordion implements ColorableComponent<AccordionSchemeConfiguration<AccordionSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: AccordionSchemeConfiguration<AccordionSchemeDefinition> = ACCORDION_SCHEME_CONFIG;
  schemeMap = ACCORDION_SCHEME_MAP;
  private paneHeaders: HTMLElement[] = [];
  private accordionContainers: HTMLElement[] = [];
  private preventInternalIndexUpdate: boolean;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: AccordionSchemeDefinition;
  @State() panes: Array<HTMLEonUiAccordionPaneElement>;
  @State() highlightedPaneIndex: number = -1;
  @State() isDesktopOrLarger: boolean;
  @State() forceRender: boolean;
  @State() internalSelectedPaneIndexes: number[] = [];

  @Element() hostElement: HTMLEonUiAccordionElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: AccordionSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Sets this to a (zero-based) index of an accordion pane to select it.
   */
  @Prop({ reflect: true, mutable: true }) selectedPaneIndex: string = "";

  /**
   * The size of the accordion component
   */
  @State() size: AccordionSize;

  /**
   * Defines whether one or more panes can be visible.
   */
  @Prop() mode: AccordionMode = ACCORDION_MODE_SINGLE;

  /**
   * Expands or collapses all panes.
   */
  @Prop({ mutable: true }) expandAll: boolean = false;

  /**
   * If set to true, the "Collapse/Expand" icons are hidden on Desktop or larger viewports and the accordion panes are always expanded.
   * This will overrule the "mode" Prop (if set to single).
   */
  @Prop() expandOnDesktop: boolean = false;

  /**
   * If set to true, the pane titles will be hidden on desktop mode if "expandOnDesktop" is set.
   */
  @Prop() hidePaneTitleOnDesktop: boolean = false;

  /**
   * Icon position
   */
  @Prop() iconPosition: "left" | "right" = "right";

  /**
   * No-Background option
   */
  @Prop() noBackground: boolean = false;

  /**
   * Raised when the accordion has been loaded.
   */
  @Event() accordionLoad: EventEmitter;

  /**
   * Raised after the active pane changed. Returns the index of the active pane.
   */
  @Event() selectedPaneChanged: EventEmitter<string>;

  /**
   * Raised after a pane has been opened.
   */
  @Event() accordionPaneOpened: EventEmitter<number>;

  /**
   * Raised after a pane has been closed.
   */
  @Event() accordionPaneClosed: EventEmitter<number>;

  componentWillLoad() {
    this.applyScheme();
    this.size = isMobile() ? "small" : isTablet() ? "regular" : "large";
    this.panes = Array.from(this.hostElement.children as HTMLCollectionOf<HTMLEonUiAccordionPaneElement>);
    this.isDesktopOrLarger = isDesktopOrLarger();
    if (this.mode === ACCORDION_MODE_SINGLE) {
      this.expandAll = false;
    }
    this.updateInternalPaneIndexes();
    this.expandOrCollapsePanes();
    this.setMultipleMode();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.accordionLoad.emit();

    detectSlotChanges(() => {
      this.panes = Array.from(this.hostElement.children as HTMLCollectionOf<HTMLEonUiAccordionPaneElement>);
    }, this.hostElement);
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.isDesktopOrLarger = isDesktopOrLarger();

    this.size = isMobile() ? "small" : isTablet() ? "regular" : "large";
  }

  @Listen("click", { target: "document" })
  onDocumentClick() {
    if (document.activeElement.tagName.toLowerCase() !== "eon-ui-accordion") {
      this.highlightPane(null);
    }
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.innerBackground || this.activeScheme.activeAccordionBackgroundColor;
  }

  @Watch("mode")
  onModeChanged() {
    if (this.mode === ACCORDION_MODE_SINGLE) {
      this.expandAll = false;
      this.expandOrCollapsePanes();
    }
  }

  @Watch("expandAll")
  @Watch("isDesktopOrLarger")
  @Watch("expandOnDesktop")
  expandOrCollapsePanes() {
    const expandAll = (this.isDesktopOrLarger && this.expandOnDesktop) || this.expandAll;
    if (this.panes && !this.selectedPaneIndex) {
      this.internalSelectedPaneIndexes = [];

      this.panes.forEach((pane, index) => {
        this.setActive(pane, expandAll);
        if (expandAll) {
          this.internalSelectedPaneIndexes.push(index);
        }
      });
    }
  }

  @Watch("expandOnDesktop")
  @Watch("expandAll")
  setMultipleMode() {
    if (this.expandAll || this.expandOnDesktop) this.mode = "multiple";
  }

  @Watch("selectedPaneIndex")
  updateInternalPaneIndexes() {
    // skip internalSelectedPaneIndexes update if selectedPaneIndex has been set based on it (prevent recursion)
    if (!this.preventInternalIndexUpdate) {
      this.internalSelectedPaneIndexes = this.selectedPaneIndex ? this.selectedPaneIndex?.split(",").map(Number) : [];
    }

    this.preventInternalIndexUpdate = false;
    this.delegatePaneStates();
  }

  delegatePaneStates() {
    for (var index = 0; index < this.panes.length; index++) {
      if (this.internalSelectedPaneIndexes.includes(index)) {
        this.openPane(index);
      } else {
        this.closePane(index);
      }
    }

    // Toggling the same pane doesn't change the selectedPaneIndex and the component wouldn't rerender without this additional state change.
    // This would lead to the "collapse/expand icon" not being updated.
    this.forceRender = !this.forceRender;
  }

  togglePaneIndexes(paneIndex: number) {
    switch (this.mode) {
      case ACCORDION_MODE_SINGLE: {
        if (this.internalSelectedPaneIndexes.includes(paneIndex)) {
          this.accordionPaneClosed.emit(paneIndex);
          this.internalSelectedPaneIndexes = [];
        } else {
          this.accordionPaneOpened.emit(paneIndex);
          this.internalSelectedPaneIndexes = [paneIndex];
          this.scrollToPane(this.panes[paneIndex]);
        }
        break;
      }
      case ACCORDION_MODE_MULTIPLE: {
        if (this.internalSelectedPaneIndexes.includes(paneIndex)) {
          this.accordionPaneClosed.emit(paneIndex);
          this.internalSelectedPaneIndexes = this.internalSelectedPaneIndexes.filter((e) => e !== paneIndex);
        } else {
          this.accordionPaneOpened.emit(paneIndex);
          this.internalSelectedPaneIndexes.push(paneIndex);
        }
        break;
      }
    }
    this.updateSelectedPaneIndexProperty();
    this.delegatePaneStates();
  }

  private openPane(paneIndex: number) {
    const pane = this.panes[paneIndex];
    this.setActive(pane, true);
  }

  private closePane(paneIndex: number) {
    const pane = this.panes[paneIndex];
    this.setActive(pane, false);
  }

  private setActive(pane: HTMLEonUiAccordionPaneElement, value: boolean) {
    pane.setAttribute("is-active", String(value));
  }

  private scrollToPane(pane) {
    window.setTimeout(() => {
      if (!this.checkVisibility(pane)) {
        pane.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);
  }

  private checkVisibility(pane) {
    const rect = pane.getBoundingClientRect();

    return rect.top >= 0 && rect.left >= 0;
  }

  private updateSelectedPaneIndexProperty() {
    this.preventInternalIndexUpdate = true;
    this.selectedPaneIndex = this.internalSelectedPaneIndexes.toString();
    this.selectedPaneChanged.emit(this.selectedPaneIndex);
  }

  private handlePaneHeaderClick(index: number) {
    this.togglePaneIndexes(index);
    if (!this.panes[index].isActive)
      this.accordionContainers[index].classList.remove(pfx("force-hover-state").toString());
  }

  private handleKeyDown(e: KeyboardEvent, index) {
    switch (e.key) {
      case KeyIds.SPACE:
      case KeyIds.ENTER:
        this.togglePaneIndexes(index);
        e.preventDefault();
        break;
      case KeyIds.TAB:
        this.highlightPane(index === this.panes.length - 1 ? -1 : index);
        break;
      default:
        break;
    }
  }

  private handlePaneHeaderFocus(index: number) {
    this.highlightPane(index);
  }

  private highlightPane(index?: number) {
    if (this.isDesktopOrLarger && this.expandOnDesktop) {
      return;
    }
    this.highlightedPaneIndex = index != null ? index : -1;
  }

  render() {
    const size = this.size;
    const showIcon = !this.isDesktopOrLarger || !this.expandOnDesktop;
    return (
      <Host>
        <div class={pfx({ "accordion-wrapper": true, [size]: true })}>
          {this.panes.map((pane: HTMLEonUiAccordionPaneElement, index: number) => {
            const paneTitle = pane.getAttribute("pane-title");
            const paneSlot = pane.getAttribute("slot");
            return (
              <div
                ref={(el) => this.accordionContainers.push(el)}
                class={pfx({
                  accordion: true,
                  active: pane.isActive,
                  "view-without-icon": !showIcon,
                  "force-hover-state": showIcon && index === this.highlightedPaneIndex,
                  "hide-shadow": this.expandOnDesktop && isDesktopOrLarger(),
                  [`icon-${this.iconPosition}`]: true,
                  "no-background": this.noBackground
                })}
              >
                <div
                  class={pfx({
                    "accordion-inner": true
                  })}
                >
                  <div
                    role={showIcon && "button"}
                    ref={(el) => this.paneHeaders.push(el)}
                    tabIndex={0}
                    class={pfx({
                      active: pane.isActive,
                      "accordion-pane": true,
                      hidden: this.expandOnDesktop && this.hidePaneTitleOnDesktop
                    })}
                    onClick={() => showIcon && this.handlePaneHeaderClick(index)}
                    onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e, index)}
                    onFocus={() => this.handlePaneHeaderFocus(index)}
                    aria-expanded={pane.isActive ? "true" : "false"}
                  >
                    <div class={pfx({ paneTitle: true })}>{paneTitle}</div>
                    {showIcon && <eon-ui-icon name="chevron_small_down" no-space="true"></eon-ui-icon>}
                  </div>
                  <div
                    class={pfx({
                      active: pane.isActive,
                      "accordion-content": true
                    })}
                  >
                    <slot name={paneSlot}></slot>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Host>
    );
  }
}
